import { useCallback, useRef, useState } from 'react';

import {
  ClientFormData,
  ClientIntegrationsFormData,
  ClientStatus,
  ClientTab,
  PaymentTerms,
} from '../types';

export interface ClientDraft {
  info?: Partial<ClientFormData>;
  integrations?: Partial<ClientIntegrationsFormData>;
}

interface DraftOperationParams<T> {
  tab: ClientTab;
  data: Partial<T>;
}

interface UseClientDraftReturn<T> {
  draft: ClientDraft;
  saveDraftData: (params: DraftOperationParams<T>) => void;
  getDraftData: (tab: ClientTab) => Partial<T> | undefined;
  clearDraft: () => void;
  hasDraftData: boolean;
  getFinalSubmissionData: () => ClientFormData;
}

/**
 * Manages client draft state across multiple modes (Create, Edit, Read) and tabs.
 * Provides persistent form data storage during client workflow.
 */
export function useClientDraft<T = ClientFormData>(): UseClientDraftReturn<T> {
  const [draft, setDraft] = useState<ClientDraft>({});
  const draftRef = useRef<ClientDraft>({});

  draftRef.current = draft;

  const saveDraftData = useCallback(({ tab, data }: DraftOperationParams<T>) => {
    setDraft(prevDraft => ({
      ...prevDraft,
      [tab]: {
        ...prevDraft[tab],
        ...data,
      },
    }));
  }, []);

  const getDraftData = useCallback((tab: ClientTab): Partial<T> | undefined => {
    return draftRef.current[tab] as Partial<T> | undefined;
  }, []);

  const clearDraft = useCallback(() => {
    setDraft({});
  }, []);

  const hasDraftData = Object.keys(draft).some(tab => {
    const tabData = draft[tab as ClientTab];
    return tabData && Object.keys(tabData).length > 0;
  });

  const getFinalSubmissionData = useCallback((): ClientFormData => {
    const infoData = draft.info || {};
    const integrationData = draft.integrations || {};

    return {
      // Info data
      name: infoData.name || '',
      avatar: infoData.avatar,
      status: infoData.status || ClientStatus.Active,
      email: infoData.email || '',
      managerId: infoData.managerId,
      paymentTerms: infoData.paymentTerms || PaymentTerms.Net30,
      billingAddress: infoData.billingAddress,
      shippingAddress: infoData.shippingAddress,

      // Integration data
      integrations: integrationData.integrations || [],
    };
  }, [draft]);

  return {
    draft,
    saveDraftData,
    getDraftData,
    clearDraft,
    hasDraftData,
    getFinalSubmissionData,
  };
}
